import styled from '@emotion/styled';
import { Space } from 'antd';
import { Button, Tabs } from 'antd';
import { HTMLAttributes, useEffect, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import equal from 'react-fast-compare';
import { getStoreCount } from './Result';
import { FV, Store, useStoreMap } from './share';
import StoreScopesForm from './StoreScopesForm';

const createInitialValues = (
  storeScopes: General.StoreScope[] = [],
  storeMap: Partial<Record<number, Store>> = {},
): FV => {
  return {
    mode: 'platform',
    storeScopes: storeScopes.filter((platformScope) => getStoreCount([platformScope])),
    assets: {
      storeMap,
      wabaMap: {},
    },
  };
};

export interface StoreScopesOwnProps {
  value?: General.StoreScope[];
  onChange?: (value: General.StoreScope[]) => void;
  departmentId?: number;
  platform?: General.PlatformUpperCaseTunnel;
  permId?: General.PermissionId;
  onCancel?: () => void;
  allowEmpty?: boolean;
  supportWa?: boolean;
  storeMap?: Partial<Record<number, Store>>;
  storeDataSource?: Store[];
  showFooterTips?: boolean;
  singleRegion?: boolean | string;
  singlePlatform?: boolean | string;
  invalidStoreIds?: {
    storeIds: number[];
    tooltips: string;
  }[];
}

export interface StoreScopesProps
  extends StoreScopesOwnProps,
    Omit<HTMLAttributes<HTMLDivElement>, 'value' | 'onChange'> {}

function StoreScopes(props: StoreScopesProps) {
  const {
    value,
    onChange,
    departmentId,
    platform,
    permId,
    onCancel,
    allowEmpty = false,
    supportWa,
    storeMap: storeMapProp,
    storeDataSource,
    showFooterTips = true,
    singleRegion,
    singlePlatform,
    invalidStoreIds,
    ...otherProps
  } = props;

  const { t } = useTranslate();

  const storeMap = useStoreMap({
    list: storeDataSource,
    map: storeMapProp,
  });

  const [formKey, setFormKey] = useState(0);

  const [initialValues, setInitialValues] = useState(() => createInitialValues(value, storeMap));

  const storeScopesRef = useRef<General.StoreScope[]>();

  useEffect(() => {
    if (!equal(value, storeScopesRef.current)) {
      setInitialValues(createInitialValues(value, storeMap));
      setFormKey((key) => key + 1);
    }
  }, [value, storeMap]);

  return (
    <StoreScopesForm
      key={formKey}
      departmentId={departmentId}
      platform={platform}
      permId={permId}
      supportWa={supportWa}
      storeDataSource={storeDataSource}
      singleRegion={singleRegion}
      singlePlatform={singlePlatform}
      initialValues={initialValues}
      invalidStoreIds={invalidStoreIds}
      onSubmit={({ storeScopes }) => {
        storeScopesRef.current = storeScopes;

        onChange?.(storeScopes);
      }}
    >
      {(form, formProps) => {
        const { values, setFieldValue, handleSubmit } = formProps;

        const header = (
          <Tabs activeKey={values.mode} onChange={(mode) => setFieldValue('mode', mode)}>
            <Tabs.TabPane key="platform" tab={t('department.按平台')} />
            <Tabs.TabPane key="import" tab={t('department.批量导入')} />
          </Tabs>
        );

        const footer = (
          <Footer size={12} align="center">
            {typeof singleRegion === 'string' && <FooterTips>{singleRegion}</FooterTips>}
            {typeof singlePlatform === 'string' && <FooterTips>{singlePlatform}</FooterTips>}
            {showFooterTips && values.mode === 'platform' && (
              <FooterTips>{t('storeSelector.footerTips')}</FooterTips>
            )}
            <Button onClick={onCancel}>{t('translation.取消')}</Button>
            <Button
              type="primary"
              disabled={!allowEmpty && getStoreCount(values.storeScopes) === 0}
              onClick={() => {
                if (onCancel) {
                  onCancel();
                }

                handleSubmit();
              }}
            >
              {t('department.确认选择')}
            </Button>
          </Footer>
        );

        return (
          <Container {...otherProps}>
            {header}
            {form}
            {footer}
          </Container>
        );
      }}
    </StoreScopesForm>
  );
}

const Container = styled.div`
  > .ant-tabs {
    padding: 0 24px;
    font-weight: 400;
    border-bottom: 1px solid var(--fett-border-default);
    .ant-tabs-nav {
      margin-bottom: 0;
      &::before {
        border-color: transparent;
      }
    }
    .ant-tabs-nav-wrap {
      height: 56px;
    }
  }
`;

const Footer = styled(Space)`
  display: flex;
  justify-content: flex-end;
  padding: 10px 16px;
  border-top: 1px solid var(--fett-border-default);
`;

const FooterTips = styled.span`
  color: var(--fett-text-tertiary);
`;

export default StoreScopes;
